import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { apiService, PeopleDropdownItem } from "../services/apiService";
import { useApiAuth } from "../hooks/useApiAuth";

interface PeopleContextType {
  people: PeopleDropdownItem[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const PeopleContext = createContext<PeopleContextType | undefined>(undefined);

export const PeopleProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [people, setPeople] = useState<PeopleDropdownItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isApiReady } = useApiAuth();

  const fetchPeople = useCallback(async () => {
    if (!isApiReady) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.getPeopleDropdown();

      if (response.success && response.data) {
        setPeople(response.data);
      } else {
        setError(response.message || "Failed to load people");
        setPeople([]);
      }
    } catch (error) {
      console.error("Error fetching people:", error);
      setError("Network error: Unable to load people");
      setPeople([]);
    } finally {
      setLoading(false);
    }
  }, [isApiReady]);

  useEffect(() => {
    fetchPeople();
  }, [fetchPeople]);

  const refetch = useCallback(() => {
    fetchPeople();
  }, [fetchPeople]);

  const value = {
    people,
    loading,
    error,
    refetch,
  };

  return (
    <PeopleContext.Provider value={value}>{children}</PeopleContext.Provider>
  );
};

export const usePeopleContext = (): PeopleContextType => {
  const context = useContext(PeopleContext);
  if (context === undefined) {
    throw new Error("usePeopleContext must be used within a PeopleProvider");
  }
  return context;
};
